import React from 'react';
import type { EventLogEntry } from '../../../../../types/council';
import type { Persona } from '../../../../../types/persona';
import type { CockpitRosterEntry } from '../cockpitDerive';
import { peerCostByPersonaId } from '../../../../trailhead/expeditionLogReducer';
import { useHillTargets } from '../../../../../hooks/useHillTargets';

export const PeersTab: React.FC<{
  sessionId: string; roster: CockpitRosterEntry[]; personasById: Record<string, Persona>;
  entries: EventLogEntry[]; darkMode: boolean;
}> = ({ sessionId, roster, personasById, entries, darkMode }) => {
  const costs = peerCostByPersonaId(entries);
  const { resolve } = useHillTargets(sessionId);   // per-peer effective hill (persona→spine→trail)
  const rows = roster.filter(r => !r.dismissed_at).map(r => {
    const c = costs.get(r.persona_id);
    const budget = r.daily_loop_budget ?? 0;
    const count = r.daily_loop_count ?? 0;
    const loopFrac = budget > 0 ? count / budget : 0;
    const hill = resolve(r.persona_id, r.spine_root_message_id ?? undefined);
    return { r, name: personasById[r.persona_id]?.name ?? (r.persona_id ?? '????????').slice(0, 8),
      cost: c?.costUsd ?? 0, model: c?.modelSlug ?? r.mode ?? '—', count, budget, loopFrac, hill: hill?.title ?? null };
  }).sort((a, b) => b.loopFrac - a.loopFrac);
  if (rows.length === 0) return <div className="px-3 py-6 text-center text-[11px] opacity-50">No active peers</div>;
  return (
    <div className="flex flex-col gap-1 px-2 py-1 text-[11px]">
      {rows.map(({ r, name, cost, model, count, budget, loopFrac, hill }) => {
        const barColor = loopFrac >= 1 ? 'bg-red-500' : loopFrac >= 0.8 ? 'bg-amber-500' : 'bg-emerald-500';
        return (
          <div key={r.persona_id} className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="font-medium truncate" style={{ minWidth: 80 }}>{name}</span>
              <span className="tabular-nums shrink-0">${cost.toFixed(2)}</span>
              <span className="inline-flex items-center gap-1 shrink-0" title="daily loop budget">
                🔁 {count}/{budget}
                <span className={`inline-block h-1.5 w-8 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <span className={`block h-1.5 rounded ${barColor}`} style={{ width: `${Math.min(1, loopFrac) * 100}%` }} />
                </span>
              </span>
              <span className="opacity-60 truncate ml-auto">{model}</span>
            </div>
            {hill && (
              <span data-testid="peer-hill" className="pl-1 opacity-70 truncate" title={hill}>⛰ {hill}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};
